import { Muxer, StreamTarget } from 'mp4-muxer';

/**
 * Narrow seam over the fMP4 muxer so it can be replaced (e.g. by Mediabunny or an in-house
 * writer) without touching the encoder pipeline. Output bytes go to `onData` in stream order;
 * segment boundaries are recovered from those bytes by `fmp4-split`, never from callback timing.
 */
export interface MuxerAdapter {
  configure(cfg: MuxerConfig): void;
  /**
   * `isSegmentStart` marks the forced keyframes; only those may start a fragment. `durationUs`, when
   * given, replaces `chunk.duration` (Chromium reports 0): the muxer needs it for the last sample of
   * a finalised fragment, where it cannot look at the next timestamp.
   */
  addVideo(
    chunk: EncodedVideoChunk,
    meta: EncodedVideoChunkMetadata | undefined,
    timestampUs: number,
    isSegmentStart: boolean,
    durationUs?: number,
  ): void;
  addAudio(chunk: EncodedAudioChunk, meta: EncodedAudioChunkMetadata | undefined, timestampUs: number): void;
  finalize(): void;
  onData: ((bytes: Uint8Array) => void) | null;
}

export interface MuxerConfig {
  video?: { width: number; height: number; codec?: 'avc' | 'av1' };
  audio?: { codec: 'aac' | 'opus'; sampleRate: number; numberOfChannels: number };
  /** Lower bound for a fragment; keep it below the segment length so forced keyframes always cut. */
  minFragmentSeconds: number;
}

export class Mp4MuxerAdapter implements MuxerAdapter {
  onData: ((bytes: Uint8Array) => void) | null = null;
  private muxer: Muxer<StreamTarget> | null = null;
  private written = 0;

  configure(cfg: MuxerConfig): void {
    if (!cfg.video && !cfg.audio) throw new Error("At least one track is required");
    const target = new StreamTarget({
      onData: (data, position) => {
        // Fragmented mode only appends once a fragment is complete; anything else would mean the
        // muxer rewrote bytes we already handed to the splitter.
        if (position !== this.written) {
          throw new Error(`mp4-muxer wrote at ${position}, expected append at ${this.written}`);
        }
        this.written += data.byteLength;
        this.onData?.(data);
      },
    });
    this.muxer = new Muxer({
      target,
      ...(cfg.video ? { video: { codec: cfg.video.codec ?? 'avc', width: cfg.video.width, height: cfg.video.height } } : {}),
      ...(cfg.audio ? { audio: cfg.audio } : {}),
      fastStart: 'fragmented',
      // The pipeline feeds video starting at exactly 0 and drops audio before it.
      firstTimestampBehavior: 'cross-track-offset',
      minFragmentDuration: cfg.minFragmentSeconds,
    });
  }

  addVideo(
    chunk: EncodedVideoChunk,
    meta: EncodedVideoChunkMetadata | undefined,
    timestampUs: number,
    isSegmentStart: boolean,
    durationUs?: number,
  ): void {
    const data = new Uint8Array(chunk.byteLength);
    chunk.copyTo(data);
    // An encoder-chosen keyframe inside a segment is muxed as a delta sample so it cannot cut a
    // fragment early; it is still decodable, only not advertised as a random access point.
    const type = isSegmentStart ? 'key' : 'delta';
    this.requireMuxer().addVideoChunkRaw(data, type, timestampUs, durationUs ?? (chunk.duration || 0), meta);
  }

  addAudio(chunk: EncodedAudioChunk, meta: EncodedAudioChunkMetadata | undefined, timestampUs: number): void {
    const data = new Uint8Array(chunk.byteLength);
    chunk.copyTo(data);
    this.requireMuxer().addAudioChunkRaw(data, chunk.type, timestampUs, chunk.duration ?? 0, meta);
  }

  finalize(): void {
    this.muxer?.finalize();
    this.muxer = null;
  }

  private requireMuxer(): Muxer<StreamTarget> {
    if (!this.muxer) throw new Error('MuxerAdapter used before configure()');
    return this.muxer;
  }
}
